import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Logout as LogoutIcon,
  FolderShared as FolderIcon,
  BarChart as ChartIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { MAX_RUBRIC_SCORE } from '../config';
import { calculateGroupScore } from '../lib/calculateGroupScore';
import { validateGroupData } from '../lib/validateGroupData';
import { exportGroupsToCSV } from '../lib/exportToCSV';

export function DashboardView({ groups, auth, onLogout, onCreateGroup }) {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [newGroupData, setNewGroupData] = useState({ name: '', number: '' });

  const { grouped, sortedTAs } = useMemo(() => {
    const g = groups.reduce((acc, gr) => {
      const ta = gr.createdBy || 'Unassigned';
      if (!acc[ta]) acc[ta] = [];
      acc[ta].push(gr);
      return acc;
    }, {});
    const sorted = Object.keys(g).sort((a, b) => {
      if (a === auth.username) return -1;
      if (b === auth.username) return 1;
      return a.localeCompare(b);
    });
    return { grouped: g, sortedTAs: sorted };
  }, [groups, auth.username]);

  const handleCreate = () => {
    onCreateGroup(newGroupData, () => {
      setOpenDialog(false);
      setNewGroupData({ name: '', number: '' });
    });
  };

  return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            CS Grading Dashboard{' '}
            <Chip label={`TA: ${auth.username}`} size="small" color="primary" sx={{ ml: 2 }} />
          </Typography>
          <Button color="inherit" startIcon={<ChartIcon />} sx={{ mr: 2 }} onClick={() => navigate('/analysis')}>
            Analysis
          </Button>
          <Button color="inherit" startIcon={<DownloadIcon />} sx={{ mr: 2 }} onClick={() => exportGroupsToCSV(groups)}>
            Export CSV
          </Button>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Box display="flex" justifyContent="space-between" mb={4}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Teams
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            New Evaluation
          </Button>
        </Box>
        {sortedTAs.map((taName) => (
          <Box key={taName} sx={{ mb: 6 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <FolderIcon color={taName === auth.username ? 'primary' : 'action'} sx={{ mr: 1 }} />
              <Typography
                variant="h5"
                color={taName === auth.username ? 'primary.main' : 'text.primary'}
                fontWeight="bold"
              >
                {taName === auth.username ? 'My Groups' : `${taName}'s Groups`}
              </Typography>
              <Chip label={grouped[taName].length} size="small" sx={{ ml: 2, bgcolor: '#eee' }} />
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              {grouped[taName].map((group) => {
                const isValid = validateGroupData(group) === null;
                const isMyGroup = group.createdBy === auth.username;
                return (
                  <Grid item xs={12} sm={6} md={4} key={group._id}>
                    <Card
                      sx={{
                        height: '100%',
                        borderLeft: isValid ? '5px solid #4caf50' : '5px solid #ff9800',
                        opacity: isMyGroup ? 1 : 0.8,
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(`/group/${group._id}`)}
                        sx={{ height: '100%', p: 2 }}
                      >
                        <CardContent>
                          <Typography variant="h5" gutterBottom>
                            Group {group.groupNumber}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            {group.groupName}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                            <Chip label={`${group.students.length} Students`} size="small" />
                            {isValid ? (
                              <Typography variant="h6" color="primary" fontWeight="bold">
                                {calculateGroupScore(group).toFixed(1)} / {MAX_RUBRIC_SCORE}
                              </Typography>
                            ) : (
                              <Box display="flex" alignItems="center" color="error.main">
                                <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="body2" fontWeight="bold">
                                  Invalid
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Container>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Start New Evaluation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Number"
            fullWidth
            variant="outlined"
            value={newGroupData.number}
            onChange={(e) => setNewGroupData({ ...newGroupData, number: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={newGroupData.name}
            onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
